import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { api } from '../api/client';
import { Card, StatusPill, Loading, ErrorNote } from '../components/ui';
import { DataTable } from '../components/DataTable';

interface Payload {
    candidate: { id: number; fullName: string; email: string | null };
    interviews: {
        id: number;
        templateName: string;
        status: string;
        reviewStatus: string;
        answered: number;
        questionCount: number;
        createdAt: string;
        scheduledAt: string | null;
        completedAt: string | null;
        tokenExpiresAt: string;
        invitationSentAt: string | null;
        invitationError: string | null;
        invitationEmail: string | null;
    }[];
}

export function CandidateInterviews() {
    const { id } = useParams();

    const { data, isLoading, error } = useQuery({
        queryKey: ['candidate-interviews', id],
        queryFn: () => api.get<Payload>(`/api/admin/candidates/${id}/interviews`)
    });

    if (isLoading) return <Loading />;
    if (error) return <ErrorNote message={(error as Error).message} />;
    if (!data) return null;

    return (
        <Card heading title={`${data.candidate.fullName} — every interview`}>
            {/* Stated plainly, because a recruiter will come here looking for a link they lost. */}
            <p className="mb-4 text-sm text-slate-600">
                Interview links are not stored — only a one-way hash of each one — so a link that was
                not copied when it was created cannot be recovered. Use{' '}
                <strong>Resend link</strong> on the interviews screen to mint a new one, which retires
                the old.
            </p>

            <DataTable
                rows={data.interviews}
                rowKey={(row) => row.id}
                empty="This candidate has not had an interview yet."
                columns={[
                    {
                        header: 'Interview',
                        cell: (row) => (
                            <Link
                                to={`/interviews/${row.id}`}
                                className="font-medium text-[var(--brand-primary)] hover:underline"
                            >
                                #{row.id}
                            </Link>
                        )
                    },
                    { header: 'Template', cell: (row) => row.templateName },
                    { header: 'Progress', cell: (row) => <StatusPill kind="status" value={row.status} /> },
                    { header: 'Review', cell: (row) => <StatusPill kind="review" value={row.reviewStatus} /> },
                    {
                        header: 'Answered',
                        cell: (row) => `${row.answered} of ${row.questionCount}`
                    },
                    {
                        header: 'Created',
                        cell: (row) => new Date(row.createdAt).toLocaleDateString()
                    },
                    {
                        header: 'Scheduled for',
                        cell: (row) => row.scheduledAt
                            ? new Date(row.scheduledAt).toLocaleString(undefined, {
                                day: 'numeric', month: 'short', year: 'numeric',
                                hour: 'numeric', minute: '2-digit'
                            })
                            : <span className="text-xs text-slate-500">—</span>
                    },
                    {
                        header: 'Link valid until',
                        cell: (row) => {
                            const expiry = new Date(row.tokenExpiresAt);
                            return expiry < new Date()
                                ? <span className="text-xs text-slate-500">expired</span>
                                : expiry.toLocaleString();
                        }
                    },
                    {
                        header: 'Invitation',
                        cell: (row) => row.invitationSentAt
                            ? <span className="text-xs text-slate-500">
                                Emailed to {row.invitationEmail}
                            </span>
                            : <span className="text-xs text-amber-700" title={row.invitationError ?? ''}>
                                Not emailed
                            </span>
                    }
                ]}
            />

            <Link
                to="/candidates"
                className="mt-4 inline-block text-sm text-[var(--brand-primary)] hover:underline"
            >
                Back to candidates
            </Link>
        </Card>
    );
}
